import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../hooks/useSettings';
import { resolveContentLayout } from '../../theme/presets';
import axios from 'axios';
import EnrichmentTypeSelector from './EnrichmentTypeSelector';
import EnrichmentCSVUpload from './EnrichmentCSVUpload';
import EnrichmentJobsTable from './EnrichmentJobsTable';
import PageHeader from './PageHeader';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Download, CheckCircle, Sparkles } from 'lucide-react';

const AIEnrichmentPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { settings, loading: settingsLoading } = useSettings();

  const contentLayout = resolveContentLayout(settings?.content_layout);
  const isFocused = contentLayout === 'focused';
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0); // 0 = Jobs List, 1 = Type Selection, 2 = CSV Upload, 3 = Success
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [createdJob, setCreatedJob] = useState(null);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch subscription data
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/user/subscription/`, { timeout: 15000 });
        setSubscription(response.data);
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    fetchSubscription();
  }, [API_BASE_URL]);

  const handleTypeSelectionNext = (types) => {
    setSelectedTypes(types);
    setCurrentStep(2);
  };

  const handleCSVUploadBack = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async (formData) => {
    try {
      setError(null);

      // Submit to backend
      const response = await axios.post(
        `${API_BASE_URL}/api/jobs/enrichment/v2`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Store job details and show success screen
      setCreatedJob(response.data);
      setCurrentStep(3);

    } catch (err) {
      console.error('Error creating enrichment job:', err);
      setError(err.response?.data?.detail || 'Failed to create enrichment job');
      throw err; // Re-throw so EnrichmentCSVUpload can handle it
    }
  };

  const handleCancel = () => {
    setCurrentStep(0);
    setSelectedTypes([]);
    setCreatedJob(null);
    setError(null);
  };

  const handleDownloadCSV = () => {
    if (createdJob && createdJob.brightdata_csv_url) {
      window.open(createdJob.brightdata_csv_url, '_blank');
    }
  };

  const handleCreateAnother = () => {
    setCurrentStep(1);
    setSelectedTypes([]);
    setCreatedJob(null);
    setError(null);
  };

  const handleStartNewJob = () => {
    setCurrentStep(1);
    setSelectedTypes([]);
    setCreatedJob(null);
    setError(null);
  };

  const handleBackToList = () => {
    setCurrentStep(0);
    setSelectedTypes([]);
    setCreatedJob(null);
    setError(null);
  };

  return (
    <div style={{
      padding: '24px',
      maxWidth: 1400,
      margin: '0 auto',
      minHeight: 'calc(100vh - 100px)'
    }}>
      {/* Step 0: Jobs List */}
      {currentStep === 0 && (
        <>
          <PageHeader
            variant={contentLayout}
            title={isFocused ? 'AI Enrichment' : 'Ready to enrich your data with AI?'}
            description={isFocused
              ? 'Enrich your contacts with AI-extracted insights'
              : 'Upload a CSV and select enrichment types to extract valuable insights'}
            buttonText="New Enrichment Job"
            onButtonClick={handleStartNewJob}
            buttonIcon={<Sparkles size={16} />}
            showButton={true}
            subscription={subscription}
            subscriptionLoading={subscriptionLoading}
            gradientFrom="#faf5ff"
            gradientTo="white"
            borderColor="#e9d5ff"
          />
          <EnrichmentJobsTable
            onCreateNew={handleStartNewJob}
            settings={settings}
          />
        </>
      )}

      {/* Step 1: Type Selection */}
      {currentStep === 1 && (
        <EnrichmentTypeSelector
          onNext={handleTypeSelectionNext}
          onCancel={handleCancel}
          initialSelection={selectedTypes}
        />
      )}

      {/* Step 2: CSV Upload */}
      {currentStep === 2 && (
        <EnrichmentCSVUpload
          selectedTypes={selectedTypes}
          onBack={handleCSVUploadBack}
          onSubmit={handleSubmit}
        />
      )}

      {/* Step 3: Success */}
      {currentStep === 3 && createdJob && (
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <Card style={{ backgroundColor: 'var(--color-success-bg)', borderColor: 'var(--color-success)' }}>
            <Card.Content>
              {/* Success Icon */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'var(--color-success)',
                margin: '0 auto 24px'
              }}>
                <CheckCircle size={36} color="#fff" />
              </div>

              {/* Success Message */}
              <h2 style={{
                textAlign: 'center',
                fontSize: 24,
                fontWeight: 700,
                color: '#065f46',
                margin: '0 0 8px 0'
              }}>
                Enrichment Job Created Successfully!
              </h2>

              <p style={{
                textAlign: 'center',
                fontSize: 16,
                color: '#047857',
                margin: '0 0 24px 0'
              }}>
                Your job has been submitted to Bright Data for processing
              </p>

              {/* Job Details */}
              <div style={{
                padding: 16,
                backgroundColor: '#ecfdf5',
                borderRadius: 8,
                marginBottom: 24
              }}>
                <div style={{ fontSize: 14, color: '#065f46', marginBottom: 8 }}>
                  <strong>Job ID:</strong>{' '}
                  <code style={{ backgroundColor: '#d1fae5', padding: '2px 6px', borderRadius: 3 }}>
                    {createdJob.id}
                  </code>
                </div>
                <div style={{ fontSize: 14, color: '#065f46', marginBottom: 8 }}>
                  <strong>Total Rows:</strong> {createdJob.total_rows?.toLocaleString() || 0}
                </div>
                <div style={{ fontSize: 14, color: '#065f46', marginBottom: 8 }}>
                  <strong>Enrichment Types:</strong> {createdJob.total_columns || 0}
                </div>
                <div style={{ fontSize: 14, color: '#065f46' }}>
                  <strong>Total Enrichments:</strong> {createdJob.total_enrichments?.toLocaleString() || 0}
                </div>
                {createdJob.brightdata_snapshot_id && (
                  <div style={{ fontSize: 14, color: '#065f46', marginTop: 8 }}>
                    <strong>Bright Data Snapshot:</strong>{' '}
                    <code style={{ backgroundColor: '#d1fae5', padding: '2px 6px', borderRadius: 3 }}>
                      {createdJob.brightdata_snapshot_id}
                    </code>
                  </div>
                )}
              </div>

              {/* What Happens Next */}
              <div style={{
                padding: 16,
                backgroundColor: 'var(--color-info-bg)',
                borderRadius: 8,
                marginBottom: 24
              }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-info-fg)', marginBottom: 12 }}>
                  📋 What Happens Next:
                </h3>
                <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--color-info-fg)', fontSize: 14, lineHeight: 1.8 }}>
                  <li>Bright Data is processing your enrichment requests</li>
                  <li>Results will be available when scraping completes</li>
                  <li>You'll be notified via webhook when results are ready</li>
                  <li>Extracted data will be processed with GPT-4o and converted to structured CSV</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 12 }}>
                <Button
                  onClick={handleDownloadCSV}
                  style={{ flex: 1, backgroundColor: 'var(--color-success)' }}
                >
                  <Download size={16} style={{ marginRight: 8 }} />
                  Download Bright Data CSV
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCreateAnother}
                  style={{ flex: 1 }}
                >
                  Create Another Job
                </Button>
              </div>

              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Button variant="secondary" onClick={handleBackToList}>
                  Back to Jobs List
                </Button>
              </div>
            </Card.Content>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AIEnrichmentPage;
